/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'
import { useIntl } from 'react-intl'

import type { EuiComboBoxOptionOption } from '@elastic/eui'
import { EuiComboBox, EuiFormRow } from '@elastic/eui'

import { CCM_ROLES } from './constants'
import messages, { roleMessages } from './messages'

import type { FC } from 'react'
import type { CCMRole } from './types'

type CCMRoleDropdownProps = {
  selectedGlobalRole: CCMRole | undefined
  onRoleChange: (value: CCMRole | undefined) => void
}

export const CCMRoleDropdown: FC<CCMRoleDropdownProps> = ({ selectedGlobalRole, onRoleChange }) => {
  const { formatMessage } = useIntl()

  const ccmRoleSelectionOptions: Array<EuiComboBoxOptionOption<CCMRole>> = CCM_ROLES.map(
    (role) => ({
      value: role,
      label: formatMessage(roleMessages[role]),
    }),
  )

  return (
    <EuiFormRow label={formatMessage(messages.allConnectedClustersRole)} fullWidth={true}>
      <EuiComboBox<CCMRole>
        placeholder={formatMessage(messages.selectRolePlaceholder)}
        singleSelection={true}
        options={ccmRoleSelectionOptions}
        onChange={(selectedOptions) => {
          onRoleChange(selectedOptions[0]?.value)
        }}
        aria-label={formatMessage(messages.globalRoleDropdownAccessibleLabel)}
        data-test-id='ccm-role-dropdown'
        selectedOptions={ccmRoleSelectionOptions.filter(
          (option) => option.value === selectedGlobalRole,
        )}
      />
    </EuiFormRow>
  )
}
